// RECOMMENDATIONS (Phase R3) — CVCRAFT_RUBRIC_CODEBOOK_v1.md §10.
//
// THE NEVER-SAY RULES ARE ENFORCED BY CONSTRUCTION, NOT BY REVIEW. §10.5 forbids "add more publications", "you need
// more leadership", "this would be more competitive if…", "programs want to see…", "consider a research year",
// "aim for first authorship", "seek more independent clinical responsibility", and "any suggestion premised on
// volume rather than documentation". None of those sentences is expressible here, because this module emits no free
// text at all: every recommendation is a pinned copy-string id or a pinned TEMPLATE id with named slots, and slots
// carry ledger values or literal placeholders. There is no string concatenation path from a model, a heuristic, or
// an author into a rendered recommendation.
//
// SOURCE RESTRICTION (§10.1). Recommendations are generated ONLY from facts that are `user_confirmed`, provenance
// `user_supplied`, or explicitly qualified in the recommendation text itself. "A recommendation MUST NOT assert an
// unconfirmed fact as true." Every recommendation carries its `entry_id` and its evidence — a span for a positive
// claim, a closed-world `search_scope` for an omission-based one — and one with no traceable evidence MUST NOT
// render.

import {
  NOT_STATED,
  type ClinicalEntryRecord,
  type EntryRecord,
  type LedgerFact,
  type NotStated,
  type SearchScope,
} from './factLedger';
import {
  APPROVED_PLACEHOLDER_LITERALS,
  PHRASING_TEMPLATES,
  type CopyStringId,
  type PhrasingTemplateId,
} from './rubricCopy';
import { expectationProfile, hasDatedSpan, type ProfileQuestionId } from './entryTypeProfiles';
import { readEntrySignals } from './factLedger';

/* ============================================================================================================== *
 * §10.3 — the three kinds, STRICTLY SEPARATED, never blended
 * ============================================================================================================== */

export type RecommendationKind = 'known_fact' | 'question_to_answer' | 'suggested_phrasing';

export const RECOMMENDATION_KIND_COPY_IDS = {
  known_fact: 'CS-REC-KIND-KNOWN',
  question_to_answer: 'CS-REC-KIND-QUESTION',
  suggested_phrasing: 'CS-REC-KIND-PHRASING',
} as const satisfies Record<RecommendationKind, CopyStringId>;

/**
 * §10.2 — THERE IS EXACTLY ONE GROUP (codebook v1.7).
 *
 * `group_b_experience_months_out` is DELETED. §10.2 through v1.6 mandated an experience-building group, while
 * §10.5 forbids "add more publications", "you need more leadership", "consider a research year", "aim for first
 * authorship", "seek more independent clinical responsibility" and "any suggestion premised on volume rather than
 * documentation"; §4.6 requires a documentation change INSTEAD of a volume suggestion; §11.5 forbids rewarding
 * output volume at all; and §1.1 scopes the whole construct to THE DOCUMENT.
 *
 * A recommendation to build new experience is necessarily a claim about what the applicant should DO rather than
 * about what their CV STATES. Every concrete instance of it is on the ban list, so the group could only ever ship
 * permanently empty — and the R3 review found the worse consequence of shipping it that way: the "suppression
 * mechanism" retained to exercise the empty group accepted an EXTERNALLY SUPPLIED array of recommendations, an
 * injection surface whose only purpose was to make an unpopulatable feature testable. Both are gone.
 */
export type TimeHorizon = 'group_a_documentation_today';

export const TIME_HORIZON_COPY_IDS = {
  group_a_documentation_today: 'CS-REC-GROUP-A',
} as const satisfies Record<TimeHorizon, CopyStringId>;

/**
 * §10.1's evidence requirement, as a type. A positive claim cites facts (C2a); an omission-based recommendation
 * — "the CV does not state when this took place" — cites the closed world it was assessed over (C2b), because an
 * omission claim is traceable to a documented scope and never to a span.
 */
export type RecommendationEvidence =
  | { kind: 'facts'; fact_ids: string[] }
  | { kind: 'closed_world'; search_scope: SearchScope };

/**
 * §10.3 suggested phrasing is TEMPLATE-AND-SLOT GENERATED ONLY. A slot is filled from exactly one ledger value
 * with provenance `user_supplied` or `user_confirmed`, or from a LITERAL placeholder.
 *
 * The literal placeholder is not a fallback that degrades the output — it is the mechanism by which the anti-
 * fabrication moat stays honest. `[your role here]` says plainly that we do not know the role, where inventing a
 * plausible one would be exactly the AF1 violation ("no invented authorship contribution").
 */
export type SlotValue =
  | { kind: 'ledger'; fact_id: string; value: string }
  | { kind: 'placeholder'; literal: string };

export interface Recommendation {
  kind: RecommendationKind;
  kind_copy_string_id: CopyStringId;
  horizon: TimeHorizon;
  entry_id: string;
  /** The pinned copy-string id (question/known-fact) — never free text. */
  copy_string_id: CopyStringId | null;
  /** The pinned, versioned template id (suggested phrasing only). AF-2 pins template text by id. */
  template_id: string | null;
  slots: Record<string, SlotValue> | null;
  evidence: RecommendationEvidence;
}

/* ============================================================================================================== *
 * §10.3 / AF-2 — the render check, WITH REAL VALUE BINDING
 * ============================================================================================================== */

/** The provenances §10.1 admits as a recommendation SOURCE. Nothing else may fill a slot or ground a known fact. */
export function isApprovedSource(fact: LedgerFact, entry: EntryRecord): boolean {
  // OWNERSHIP IS THE FIRST CONDITION, AND IT APPLIES TO `user_supplied` TOO (round-2 blocker 5). The previous
  // version returned true for any `user_supplied` fact without asking whose it was, so `buildKnownFact(entryA,
  // factFromEntryB)` produced a known fact ATTRIBUTED TO ENTRY A that restated another experience's value — a
  // recommendation asserting something about the wrong entry, with a citation that resolves (the fact is real) and
  // proves nothing (it is not this entry's). §10.1's "every recommendation carries its `entry_id` and its evidence"
  // is meaningless if the evidence can belong to a different entry_id. That the fact came from the user makes it a
  // more trustworthy VALUE, not a fact about a different experience.
  if (!entry.facts.some((f) => f.fact_id === fact.fact_id)) return false;
  if (fact.provenance === 'user_supplied') return true;
  // `user_confirmed` is a state of the ENTRY (§2.8): the user has reviewed the extracted facts and said we read
  // them right. That is what promotes an `extracted_span` fact to an approved source.
  return entry.confirmation_state === 'user_confirmed' && fact.provenance === 'extracted_span';
}

export type RenderFailure =
  | 'unresolved_slot'
  | 'unapproved_slot_provenance'
  | 'no_traceable_evidence'
  | 'unregistered_template'
  | 'slot_value_not_ledger_value'
  | 'slot_fact_not_owned_by_entry'
  | 'slot_fact_absent_from_evidence'
  | 'unapproved_placeholder';

export interface RenderCheck {
  renderable: boolean;
  reason?: RenderFailure;
  detail?: string;
}

/** The ledger context AF-2 needs to check a render. Facts, not just their ids — see `checkPhrasingRenderable`. */
export interface PhrasingLedgerContext {
  /** The recommendation's own entry. Slot facts must belong to it. */
  entry: EntryRecord;
  /** Every fact on that entry, by id. */
  factsById: ReadonlyMap<string, LedgerFact>;
}

/**
 * AF-2's render check. §10.3: "the render is valid iff every filled slot resolves to a confirmed ledger fact id"
 * — and v1.7's re-authored T-AF-2 spells out the six further conditions that sentence was always assuming.
 *
 * WHY THE SIGNATURE CHANGED. The previous version took a bare `ReadonlySet<string>` of approved ids and checked
 * membership in it. Membership is the ONE thing that is not sufficient, because a slot carries a `value` as well
 * as a `fact_id`, and nothing compared them: an approved `role_title` fact id paired with the fabricated value
 * "Led 500 independent procedures" passed the check and rendered. That is precisely the AF1 violation — invented
 * independence, invented volume — that this moat exists to stop, waved through by the moat itself. A check that
 * cannot see the fact's VALUE cannot detect substitution, so it now receives the facts.
 *
 * The seven conditions, each blocking independently and each asserted one at a time by T-AF-2:
 *   1. the template id is registered in Appendix A.3;
 *   2. every slot the template names is filled;
 *   3. a `ledger` slot's fact EXISTS and has approved provenance (§10.1);
 *   4. its `value` EQUALS the fact's stored ledger value;
 *   5. the fact is OWNED by this recommendation's entry;
 *   6. the recommendation's evidence list CONTAINS the slot's fact id;
 *   7. a `placeholder` slot's literal is one of the four in Appendix A.4.
 *
 * Fails CLOSED throughout: a slot we cannot fully verify is not rendered with a blank or a guess; the whole
 * recommendation is withheld.
 */
export function checkPhrasingRenderable(rec: Recommendation, ctx: PhrasingLedgerContext): RenderCheck {
  if (rec.kind !== 'suggested_phrasing') return { renderable: true };
  // (5, PRECONDITION) THE CONTEXT MUST BE THIS RECOMMENDATION'S OWN ENTRY (round-2 blocker 5). Condition 5 checks
  // slot facts against `ctx.entry.facts`, which establishes ownership only if `ctx.entry` IS the entry the
  // recommendation names. Called with a mismatched pair, the old check happily verified that entry B owns the
  // slot fact and then rendered the result under entry A's id — ownership verified against the wrong owner.
  if (rec.entry_id !== ctx.entry.entry_id) {
    return {
      renderable: false,
      reason: 'slot_fact_not_owned_by_entry',
      detail: `recommendation names entry ${rec.entry_id} but was checked against entry ${ctx.entry.entry_id}`,
    };
  }
  if (rec.template_id === null || rec.slots === null) {
    return { renderable: false, reason: 'unresolved_slot', detail: 'suggested phrasing with no template or slots' };
  }

  // (1) The template must be one Appendix A.3 registers. An unregistered template is unreviewed text.
  const template = PHRASING_TEMPLATES[rec.template_id as PhrasingTemplateId];
  if (template === undefined) {
    return { renderable: false, reason: 'unregistered_template', detail: `template ${rec.template_id} is not registered in Appendix A.3` };
  }

  // (7, part) `no_traceable_evidence` was DECLARED in the type and never enforced. §10.1 is unconditional: "a
  // recommendation with no traceable evidence MUST NOT render."
  const evidenceIds = rec.evidence.kind === 'facts' ? rec.evidence.fact_ids : [];
  if (rec.evidence.kind === 'facts' && evidenceIds.length === 0) {
    return { renderable: false, reason: 'no_traceable_evidence', detail: 'suggested phrasing cites zero facts' };
  }

  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    const name = match[1];
    // (2) Every named slot filled. An unmentioned slot would render the literal "{role}" to the user — a broken
    // promise rather than an honest placeholder.
    const slot = rec.slots[name];
    if (slot === undefined) {
      return { renderable: false, reason: 'unresolved_slot', detail: `slot ${name} not filled` };
    }

    if (slot.kind === 'placeholder') {
      // (7) A CLOSED set. Without this, an arbitrary fabricated sentence passes AF-2 by declaring itself a
      // placeholder — the check's second bypass, and the one that needs no valid fact id at all.
      if (!APPROVED_PLACEHOLDER_LITERALS.includes(slot.literal)) {
        return {
          renderable: false,
          reason: 'unapproved_placeholder',
          detail: `slot ${name} carries ${JSON.stringify(slot.literal)}, which is not one of the Appendix A.4 placeholders`,
        };
      }
      continue;
    }

    // (5) Ownership, CHECKED FIRST so it reports its own reason. A fact from ANOTHER entry is a fact about another
    // experience; rendering it inside this entry's suggested phrasing attributes it to the wrong thing. Ownership
    // is also the first condition inside `isApprovedSource` now (blocker 5), so checking provenance first would
    // collapse every cross-entry citation into the vaguer `unapproved_slot_provenance` — T-AF-2 requires each
    // condition to block INDEPENDENTLY and be assertable one at a time, which needs the specific reason preserved.
    const fact = ctx.factsById.get(slot.fact_id);
    if (fact === undefined || !ctx.entry.facts.some((f) => f.fact_id === slot.fact_id)) {
      return {
        renderable: false,
        reason: 'slot_fact_not_owned_by_entry',
        detail: `slot ${name} cites ${slot.fact_id}, which is not a fact on entry ${ctx.entry.entry_id}`,
      };
    }
    // (3) The fact must be an approved source.
    if (!isApprovedSource(fact, ctx.entry)) {
      return {
        renderable: false,
        reason: 'unapproved_slot_provenance',
        detail: `slot ${name} cites ${slot.fact_id}, which is not a resolvable user_confirmed or user_supplied fact`,
      };
    }
    // (4) THE VALUE BINDING. The slot renders `slot.value`; the evidence is `fact.value`. If they differ, the
    // rendered sentence says something the cited fact does not, and the citation is decoration.
    const ledgerValue = fact.provenance === 'user_supplied' ? fact.user_value : fact.value;
    if (String(ledgerValue) !== slot.value) {
      return {
        renderable: false,
        reason: 'slot_value_not_ledger_value',
        detail: `slot ${name} renders ${JSON.stringify(slot.value)} but fact ${slot.fact_id} holds ${JSON.stringify(String(ledgerValue))}`,
      };
    }
    // (6) The slot's fact must be among the evidence the recommendation offers, or "show source" opens onto a
    // list that does not contain the thing the sentence was built from.
    if (!evidenceIds.includes(slot.fact_id)) {
      return {
        renderable: false,
        reason: 'slot_fact_absent_from_evidence',
        detail: `slot ${name} cites ${slot.fact_id}, which is absent from the recommendation's evidence list`,
      };
    }
  }
  return { renderable: true };
}

/* ============================================================================================================== *
 * §8.2 / §10.3 — the questions to answer
 *
 * "Not a flag — routed as a question to answer. Unstated dates, unstated duration, and unstated clinical stage or
 * licence context are handled as Questions to answer, NOT as flags, because there is nothing to clarify — the
 * information simply is not there."
 * ============================================================================================================== */

function isStated<T>(v: T | NotStated): v is T {
  return v !== NOT_STATED && v !== undefined && v !== null && v !== '';
}

/**
 * `CS-Q-DATE-END` — THE PARTIAL-DATE QUESTION. REGISTERED 2026-08-23 (A.5-P.25); this local constant is the
 * typed handle the routing above uses.
 *
 * ITS TEXT, as registered — see `rubricCopy.ts` and codebook A.5-P.25 for the wording's own rationale:
 *   'Your entry states one date. If this ran over a period, adding when it ended — or that it is ongoing — makes that clear.'
 * It obeys the §4.7 phrasing ban by construction — no "missing", no "gap", no "you have not" — and its second
 * sentence states what the entry DOES say, which is the whole point of asking the narrow question.
 *
 * WHY THE CAST, AND WHAT IT COSTS. `rubricCopy.ts` is owned by another agent this session, so this module may not
 * register the id itself; `CopyStringId` is `keyof typeof COPY`, so the literal does not typecheck without the
 * registration. The cast is the ONLY way to express the routing without editing a file I do not own.
 *
 * ORDERING DEPENDENCY — THIS MUST NOT REACH THE SURFACE BEFORE THE REGISTRATION LANDS. Until `COPY` carries the
 * id, `COPY['CS-Q-DATE-END']` resolves to `undefined` on the client, and the adapter's `copyText` is not written
 * for an unregistered id. The routing and the registration are ONE change and must ship together; nothing here is
 * deployed. `T-METRIC-6` below carries a live check that upgrades to a real assertion the moment the id is
 * registered, so the cast cannot be forgotten in place.
 */
export const CS_Q_DATE_END = 'CS-Q-DATE-END' as CopyStringId;

function omissionScope(
  claim_type: string,
  entry: EntryRecord,
  fields: string,
  assessmentVersion: string,
): SearchScope {
  return {
    claim_type,
    scope_description: `entry ${entry.entry_id}: ${fields} assessed; not stated`,
    entry_ids_assessed: [entry.entry_id],
    n_assessed: 1,
    extractor_version: assessmentVersion,
  };
}

export interface RecommendationInput {
  entries: readonly EntryRecord[];
  clinical: readonly { entry_id: string; record: ClinicalEntryRecord }[];
  /**
   * §10.2. The applicant-stated deadline (`YYYY-MM-DD`), or null.
   *
   * RETAINED AS A FIELD, BUT IT NO LONGER GATES ANYTHING (v1.7). A stated deadline is a legitimate thing for an
   * applicant to tell us and callers already pass it; what is gone is the suppressor it used to drive. It is
   * still NEVER inferred from the CaRMS cycle or any other source, which now holds trivially, since no
   * recommendation depends on a deadline at all. T-REC-1 asserts the recommendation set is IDENTICAL across a
   * no-deadline, a distant-deadline and a tomorrow-deadline fixture.
   */
  statedDeadline: string | null;
  /** `YYYY-MM-DD`. Configuration, never a ledger fact, never displayed. */
  reviewDate: string;
  assessmentVersion: string;
  /**
   * R5.8 — §2.7 C2a's valid-span id set, the same one `assessCv` receives. Read for ONE purpose: the
   * `clinical_employment` profile offers `CS-Q-CLINICAL-CONTEXT` only where the entry STATES independent practice
   * (OQ-2), and "states" is the `stated_independent_practice` signal read under C2a. Optional and FAIL-CLOSED: a
   * caller that does not pass it gets no signal reading, so the signal-gated question is simply not offered there.
   */
  validSpanFactIds?: ReadonlySet<string>;
}

export interface RecommendationSet {
  /**
   * The ONLY group (§10.2, v1.7). There is no second array, no empty-group placeholder, and no note explaining an
   * absence — there is nothing absent to explain, because documentation review is what the product does.
   */
  group_a: Recommendation[];
  group_a_heading_copy_string_id: 'CS-REC-GROUP-A';
}

export function buildRecommendations(input: RecommendationInput): RecommendationSet {
  const clinicalByEntryId = new Map(input.clinical.map((c) => [c.entry_id, c.record]));
  const group_a: Recommendation[] = [];

  const question = (
    entry: EntryRecord,
    copy_string_id: CopyStringId,
    scope: SearchScope,
  ): Recommendation => ({
    kind: 'question_to_answer',
    kind_copy_string_id: RECOMMENDATION_KIND_COPY_IDS.question_to_answer,
    // Every question is a documentation change the applicant can make today — that is what makes Group A "the
    // product's core value and always available" (§10.2).
    horizon: 'group_a_documentation_today',
    entry_id: entry.entry_id,
    copy_string_id,
    template_id: null,
    slots: null,
    evidence: { kind: 'closed_world', search_scope: scope },
  });

  for (const entry of input.entries) {
    // R5.8 — THE EXPECTATION PROFILE GATES EACH QUESTION BEFORE IT IS CONSTRUCTED (spec §2.2, rev 2). A question the
    // entry's profile does not allow is never built, so no closed-world scope is minted for it (C2b holds
    // trivially). The generic profile allows all three, which is today's behaviour. The profile reads the same
    // ledger fields the bands do and nothing else, so it is paraphrase- and name-invariant by construction.
    const clinicalRecord = clinicalByEntryId.get(entry.entry_id) ?? null;
    const profile = expectationProfile(entry, clinicalRecord, null);
    const allowed = (q: ProfileQuestionId) => profile.questions_allowed.includes(q);

    // Unstated dates. Rendered as a QUESTION WITH AN INPUT FIELD, never as an assertion (§10.3) — and never as
    // "missing", "gap", or "you have not" (§4.7's banned phrasings, T-BAN-3).
    //
    // PARTIAL DATES, 2026-08-23 (owner, verbatim: "Associate Physician role has 2023 attached. it should recognize
    // that and ask for an end date if needed"). ONE profile allowance (`CS-Q-DATES`) governs BOTH shapes, because
    // the end-date question is not a new question — it is the dates question narrowed to the half the entry has
    // not answered. That is deliberate: a second `ProfileQuestionId` would let the table say "may ask when" and
    // "may ask until when" separately, and there is no convention on a Canadian medical CV that expects one and
    // not the other. So `PROFILE_TABLE` is untouched by this change and no row can drift between the two.
    //
    // THE FOUR SHAPES, and why each answers as it does:
    //   no dates at all         → the generic "When did this take place?" — nothing is known, so ask broadly.
    //   START ONLY ("2023")     → ONLY the missing half, AND ONLY IF THE ENTRY'S CONVENTION IS A SPAN (the
    //                             profile's `end_date_question_applies`). Asking "when did this take place?" of an
    //                             entry that says 2023 reads as not having looked at the CV — the owner's live
    //                             case. But asking a POINT-IN-TIME credential when it ended is worse than boring:
    //                             an LMCC dated 2023 is permanent, and the question implies to the applicant that
    //                             it lapses. Live regression 2026-08-23 — Certifications went from 0 points to 8,
    //                             every one of them this question. Where the flag is FALSE the entry is asked
    //                             NOTHING about dates, because the date it states is the date convention expects.
    //   start + end             → nothing. The span is stated.
    //   start + `is_ongoing`    → nothing. "2023 – present" is a COMPLETE span; the open end is the answer, not a
    //                             gap, and asking "when did it end?" of a current role is asking it to be wrong.
    //
    // END-ONLY (an end date with no start) IS UNCHANGED — it asks nothing, exactly as before. It is vanishingly
    // rare, no convention establishes what to ask for it, and inventing one here would be a behaviour change
    // nobody ruled on. Flagged in the report rather than decided silently.
    // Set when the narrowed end-date question fires, and read by the duration question below so the two cannot
    // both be asked of the same entry — see the suppression comment there.
    let askedEndDate = false;
    const hasStart = isStated(entry.date_start);
    const hasEnd = isStated(entry.date_end);
    const isOngoing = entry.is_ongoing === true;
    if (allowed('CS-Q-DATES')) {
      if (!hasStart && !hasEnd) {
        group_a.push(
          question(
            entry,
            'CS-Q-DATES',
            omissionScope('dates_not_stated', entry, '`date_start` and `date_end`', input.assessmentVersion),
          ),
        );
      } else if (hasStart && !hasEnd && !isOngoing && profile.end_date_question_applies) {
        askedEndDate = true;
        // The scope names the fields THIS claim was assessed over — `date_end` and `is_ongoing`, not `date_start`,
        // which the entry states. C2b: the disclosure must describe the closed world the omission was found in.
        group_a.push(
          question(
            entry,
            CS_Q_DATE_END,
            omissionScope('end_date_not_stated', entry, '`date_end` and `is_ongoing`', input.assessmentVersion),
          ),
        );
      }
    }
    // Unstated duration OR frequency — one question covers both, as §8.2's string does. Never offered where the
    // profile treats the dated span as the duration (clinical, scholarly, award, certification, membership, a
    // one-off role), and for non-clinical employment only where the entry states no dated span (spec §1 rev 2).
    // AND NEVER ALONGSIDE THE END-DATE QUESTION (2026-08-23, found on the owner's live CV). A research project
    // stating only "2022" was asked BOTH "Is this ongoing, or when did it end?" AND "How long or how often was
    // this?" — which is one question twice: the end date IS the duration for an entry that already states its
    // start, so answering either answers both. Two prompts for one gap is exactly the padding the owner has been
    // objecting to all day, and it also let the weaker of the two become the section's headline. The end-date
    // question wins because it is the more specific ask and names what the entry is actually missing.
    const durationGate = profile.duration_question_requires_no_dated_span ? !hasDatedSpan(entry) : true;
    if (!askedEndDate && allowed('CS-Q-DURATION') && durationGate && !isStated(entry.duration_stated) && !isStated(entry.frequency)) {
      group_a.push(
        question(
          entry,
          'CS-Q-DURATION',
          omissionScope('duration_not_stated', entry, '`duration_stated` and `frequency`', input.assessmentVersion),
        ),
      );
    }
    // §3.5 S7 — the ONLY thing we may ask of a clinical entry is that its stated context be made clearer. The
    // system MUST NOT recommend that any applicant seek, claim, or describe greater clinical independence, and the
    // pinned string is explicit that answering is OPTIONAL.
    //
    // R5.8 / OQ-2: on the `clinical_employment` profile the question is offered only where independent practice is
    // STATED (the C2a-valid `stated_independent_practice` signal) — otherwise a conventional clinical post would be
    // asked for context it never claimed to need. Every other profile that allows the question keeps today's rule.
    const clinical = clinicalByEntryId.get(entry.entry_id);
    const independenceGate = profile.clinical_context_question_requires_stated_independence
      ? input.validSpanFactIds !== undefined
        && readEntrySignals(entry, input.validSpanFactIds).states.stated_independent_practice.state === 'positive'
      : true;
    if (
      allowed('CS-Q-CLINICAL-CONTEXT') &&
      independenceGate &&
      clinical !== undefined &&
      !isStated(entry.training_stage_at_time) &&
      !isStated(clinical.licence_status_at_time)
    ) {
      group_a.push(
        question(
          entry,
          'CS-Q-CLINICAL-CONTEXT',
          omissionScope(
            'clinical_context_not_stated',
            entry,
            '`training_stage_at_time` and `licence_status_at_time`',
            input.assessmentVersion,
          ),
        ),
      );
    }
  }

  return {
    group_a,
    group_a_heading_copy_string_id: 'CS-REC-GROUP-A',
  };
}

/**
 * §10.3 — a KNOWN FACT: something the ledger establishes, restated, with its source evidence.
 *
 * Built only from an approved source (§10.1), which is why this is a separate constructor rather than part of the
 * sweep above: the sweep runs over every entry, while a known fact requires the user to have confirmed or supplied
 * the value first. Returns null rather than a degraded recommendation when the source is not approved — a
 * recommendation with no traceable evidence MUST NOT render.
 */
export function buildKnownFact(entry: EntryRecord, fact: LedgerFact): Recommendation | null {
  // REFUSES A CROSS-ENTRY FACT (round-2 blocker 5). This is now enforced inside `isApprovedSource`, whose first
  // condition is ownership — but the refusal is stated here too because it is this constructor's contract, not an
  // incidental consequence of its source check: a known fact restates what the ledger establishes ABOUT THIS
  // ENTRY, and a fact from another entry establishes nothing about this one however well provenanced it is.
  if (!isApprovedSource(fact, entry)) return null;

  // IT MUST CARRY RENDERABLE TEXT. §10.3 says a known fact is "rendered as a statement with its source evidence",
  // and the first R3 draft returned `copy_string_id: null` AND `template_id: null` — a recommendation carrying no
  // copy id, no template id, and no slots, i.e. nothing to render. Under T-COPY-1 ("every rendered string resolves
  // to an id in Appendix A") such an object cannot be displayed at all, so the constructor produced an object that
  // was correct about its evidence and impossible to show.
  //
  // It restates the fact through the SAME pinned `RT-*` template machinery suggested phrasing uses, because a
  // known fact IS a restatement — the only difference is that the ledger already establishes it. That also means
  // it inherits AF-2's render check unchanged rather than needing a second, weaker one.
  const value = fact.provenance === 'user_supplied' ? fact.user_value : fact.value;
  return {
    kind: 'known_fact',
    kind_copy_string_id: RECOMMENDATION_KIND_COPY_IDS.known_fact,
    horizon: 'group_a_documentation_today',
    entry_id: entry.entry_id,
    copy_string_id: null,
    template_id: 'RT-ROLE-ON-ENTRY-V1',
    slots: {
      title: { kind: 'ledger', fact_id: fact.fact_id, value: String(value) },
      // Dates and role are filled from the ledger where the entry states them, and from the Appendix A.4 literal
      // placeholders where it does not. A placeholder here is the honest answer: inventing a plausible role is
      // exactly the AF1 fabrication the placeholder mechanism exists to prevent.
      dates: slotFromField(entry, 'date_start') ?? { kind: 'placeholder', literal: '[dates here]' },
      role: slotFromField(entry, 'role_title') ?? { kind: 'placeholder', literal: '[your role here]' },
    },
    evidence: {
      kind: 'facts',
      // DEDUPLICATED. Now that the restated fact must itself be ON the entry (blocker 5), it is frequently the same
      // fact the `role_title` slot resolves to — and listing one fact twice makes "show source" open onto the same
      // row repeated, which reads as two independent pieces of evidence for a claim that has one.
      fact_ids: [
        ...new Set([
          fact.fact_id,
          ...['date_start', 'role_title']
            .map((f) => entry.facts.find((x) => x.field === f && isApprovedSource(x, entry))?.fact_id)
            .filter((id): id is string => id !== undefined),
        ]),
      ],
    },
  };
}

/**
 * A `ledger` slot for one of the entry's fields, or null when the entry states no APPROVED-SOURCE fact for it.
 *
 * Returns null rather than an unapproved slot so the caller substitutes a literal placeholder: §10.1 admits only
 * `user_confirmed`/`user_supplied` facts as a recommendation source, and a field the user has not confirmed is a
 * field we may not assert. The slot's `value` is copied from the fact itself, which is what makes AF-2's
 * value-binding check pass by construction here rather than by luck.
 */
function slotFromField(entry: EntryRecord, field: string): SlotValue | null {
  const fact = entry.facts.find((f) => f.field === field && isApprovedSource(f, entry));
  if (fact === undefined) return null;
  const value = fact.provenance === 'user_supplied' ? fact.user_value : fact.value;
  return { kind: 'ledger', fact_id: fact.fact_id, value: String(value) };
}
